import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import type { AnalyticsSummary } from "@/api/client";
import { APPROVALS_PERMISSIONS } from "@/app/nav";

type Key = keyof AnalyticsSummary["attention"];

const CARDS: { key: Key; label: string; permission: string }[] = [
  { key: "batches_pending_approval", label: "Batches awaiting approval", permission: "batch.approve" },
  { key: "payments_unconfirmed", label: "Payments awaiting confirmation", permission: "payment.read" },
  { key: "revocations_pending", label: "Revocations pending", permission: "certificate.revoke.approve" },
  { key: "reissues_pending", label: "Re-issues pending", permission: "certificate.reissue.approve" },
  { key: "renders_failed", label: "Failed renders", permission: "certificate.read" },
  { key: "emails_failed", label: "Failed emails", permission: "certificate.read" },
  { key: "print_spools_failed", label: "Failed print spools", permission: "certificate.print" },
];

// A card links only when the viewer can open the page it points to.
const canOpen = (key: Key, can: (p: string) => boolean): boolean => {
  switch (key) {
    case "batches_pending_approval":
      return APPROVALS_PERMISSIONS.every(can);
    case "print_spools_failed":
      return can("certificate.print") && can("certificate.read");
    default:
      return true;
  }
};

const LINK = "text-xs font-semibold text-brand-ink underline underline-offset-2 hover:text-brand-strong";

// A switch rather than a table: each route has its own typed search params.
function CardLink({ cardKey, label }: { cardKey: Key; label: string }): ReactNode {
  const name = `Review ${label}`;
  switch (cardKey) {
    case "batches_pending_approval":
      return <Link to="/approvals" aria-label={name} className={LINK}>Review</Link>;
    case "payments_unconfirmed":
      return <Link to="/payments" search={{ status: "unconfirmed" }} aria-label={name} className={LINK}>Review</Link>;
    case "revocations_pending":
      return <Link to="/approvals/revocations" aria-label={name} className={LINK}>Review</Link>;
    case "reissues_pending":
      return <Link to="/approvals/reissues" aria-label={name} className={LINK}>Review</Link>;
    case "emails_failed":
      return <Link to="/deliveries" search={{ status: "failed" }} aria-label={name} className={LINK}>Review</Link>;
    case "renders_failed":
    case "print_spools_failed":
      return <Link to="/batches" aria-label={name} className={LINK}>Review</Link>;
  }
}

export function AttentionCards({ attention, can }: { attention: AnalyticsSummary["attention"]; can: (p: string) => boolean }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {CARDS.map(({ key, label, permission }) => {
        const count = attention[key];
        const active = count > 0;
        return (
          <div
            key={key}
            className={`rounded-md border px-3 py-2.5 ${active ? "border-brand/30 bg-white" : "border-zinc-200 bg-zinc-50"}`}
          >
            <div className="text-xs font-medium text-zinc-500">{label}</div>
            <div className={`mt-1 text-xl font-semibold leading-none ${active ? "text-zinc-950" : "text-zinc-500"}`}>{count}</div>
            <div className="mt-2 min-h-4">
              {active && can(permission) && canOpen(key, can) ? (
                <CardLink cardKey={key} label={label} />
              ) : count === 0 ? (
                <span className="text-xs text-zinc-400">No action</span>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
